import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { LuCalendar, LuX, LuTrash2, LuChevronDown, LuChevronRight, LuGraduationCap, LuPlus, LuSearch, LuUser, LuPencil, LuBook } from 'react-icons/lu';
import { generateSemestersUntilCurrent } from '../../utils/semesterUtils';
import DistributionsChecklist from '../../components/specific/DistributionsChecklist';
import GradYearModal from '../../components/specific/GradYearModal';

/**
 * Schedule Builder page component for creating course schedules
 */
const ScheduleBuilderPage = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [allSchedules, setAllSchedules] = useState({});
  const [saving, setSaving] = useState(false);
  const [showGradYearModal, setShowGradYearModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [expandedSemesters, setExpandedSemesters] = useState(new Set());
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [selectedSemesterForAdd, setSelectedSemesterForAdd] = useState(null);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCourseData, setManualCourseData] = useState({
    courseCode: '',
    title: '',
    description: '',
    instructors: [''],
    requirements: [],
    departmentCode: ''
  });

  // Fallback timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, []);

  // Load user profile and check for graduation year
  const loadUserProfile = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const profileRef = doc(db, `colleges/${currentUser.email.split('@')[1]}/users/${currentUser.email}`);
      const profileDoc = await getDoc(profileRef);
      
      if (profileDoc.exists()) {
        const profile = profileDoc.data();
        setUserProfile(profile);
        
        if (!profile.gradYear) {
          setShowGradYearModal(true);
        } else {
          const semesterList = generateSemestersUntilCurrent(profile.gradYear);
          setSemesters(semesterList);
          if (semesterList.length > 0) {
            // Set current semester as expanded by default
            const currentSem = semesterList.find(s => s.isCurrent) || semesterList[semesterList.length - 1];
            setExpandedSemesters(new Set([currentSem?.id]));
          }
        }
      } else {
        setShowGradYearModal(true);
        setLoading(false); // Stop loading to show the modal
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setLoading(false); // Stop loading even on error
    }
  }, [currentUser]);

  // Redirect to login if not authenticated and load user profile
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    
    loadUserProfile();
  }, [currentUser, navigate, loadUserProfile]);

  // Handle graduation year submission
  const handleGradYearSubmit = async (gradYear) => {
    if (!currentUser) return;
    
    try {
      const profileRef = doc(db, `colleges/${currentUser.email.split('@')[1]}/users/${currentUser.email}`);
      
      await setDoc(profileRef, {
        email: currentUser.email,
        gradYear: gradYear,
        updatedAt: new Date()
      }, { merge: true });
      
      setUserProfile(prev => ({ ...prev, gradYear }));
      
      const semesterList = generateSemestersUntilCurrent(gradYear);
      setSemesters(semesterList);
      
      if (semesterList.length > 0) {
        const currentSem = semesterList.find(s => s.isCurrent) || semesterList[semesterList.length - 1];
        setExpandedSemesters(new Set([currentSem?.id]));
      }
    } catch (error) {
      console.error('Error saving graduation year:', error);
      throw error;
    }
  };

  // Load all schedules for all semesters
  const loadAllSchedules = useCallback(async () => {
    if (!currentUser || semesters.length === 0) return;
    
    try {
      const schedules = {};
      
      for (const semester of semesters) {
        const scheduleRef = doc(db, `colleges/${currentUser.email.split('@')[1]}/users/${currentUser.email}/schedules/${semester.id}`);
        const scheduleDoc = await getDoc(scheduleRef);
        
        if (scheduleDoc.exists()) {
          const scheduleData = scheduleDoc.data();
          schedules[semester.id] = scheduleData.courses || [];
        } else {
          schedules[semester.id] = [];
        }
      }
      
      setAllSchedules(schedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
    }
  }, [currentUser, semesters]);


  // Remove course from specific semester schedule
  const removeCourseFromSchedule = async (courseId, semesterId) => {
    const currentSchedule = allSchedules[semesterId] || [];
    const newSchedule = currentSchedule.filter(course => course.id !== courseId);
    
    // Update local state
    setAllSchedules(prev => ({
      ...prev,
      [semesterId]: newSchedule
    }));
    
    // Auto-save after removing course
    try {
      setSaving(true);
      const scheduleRef = doc(db, `colleges/${currentUser.email.split('@')[1]}/users/${currentUser.email}/schedules/${semesterId}`);
      await setDoc(scheduleRef, {
        courses: newSchedule,
        lastUpdated: new Date(),
        term: semesterId
      });
    } catch (err) {
      console.error('Error auto-saving schedule:', err);
      setError('Course removed but failed to save automatically');
    } finally {
      setSaving(false);
    }
  };


  // Load schedules when semesters are available
  useEffect(() => {
    const loadData = async () => {
      if (semesters.length > 0) {
        try {
          await loadAllSchedules();
        } catch (error) {
          console.error('Error loading schedules:', error);
          setError('Failed to load schedules');
        } finally {
          setLoading(false);
        }
      } else if (semesters.length === 0 && !showGradYearModal) {
        // If no semesters and no grad year modal, we're done loading
        setLoading(false);
      }
    };
    
    loadData();
  }, [semesters, currentUser, loadAllSchedules, showGradYearModal]);

  // Get all courses from all user schedules for distributions calculation
  const getAllUserCourses = () => {
    const allCourses = [];
    Object.values(allSchedules).forEach(schedule => {
      allCourses.push(...schedule);
    });
    return allCourses;
  };

  // Toggle semester expansion
  const toggleSemesterExpansion = (semesterId) => {
    const newExpanded = new Set(expandedSemesters);
    if (newExpanded.has(semesterId)) {
      newExpanded.delete(semesterId);
    } else {
      newExpanded.add(semesterId);
    }
    setExpandedSemesters(newExpanded);
  };


  // Load courses for search from Firestore
  const loadCourses = useCallback(async (term = 'F25') => {
    setCoursesLoading(true);
    try {
      console.log(`Loading courses for term: ${term}`);
      
      // Get the term document from /courses/{termId}
      const termRef = doc(db, 'courses', term);
      const termDoc = await getDoc(termRef);
      
      if (!termDoc.exists()) {
        console.log(`No courses found for term ${term}`);
        setCourses([]);
        setFilteredCourses([]);
        return;
      }
      
      const termData = termDoc.data();
      const departments = termData.departments || [];
      
      console.log(`Found ${departments.length} departments for term ${term}:`, departments);
      
      // Fetch courses from all department subcollections
      const allCourses = [];
      
      for (const departmentCode of departments) {
        try {
          const departmentRef = collection(db, 'courses', term, departmentCode);
          const departmentSnapshot = await getDocs(departmentRef);
          
          departmentSnapshot.forEach((courseDoc) => {
            const courseData = courseDoc.data();
            allCourses.push({
              id: courseDoc.id,
              ...courseData,
              // Ensure we have required fields
              fullCourseId: courseData.fullCourseId || courseDoc.id,
              courseCode: courseData.courseCode || courseDoc.id,
              title: courseData.title || 'Unknown Title',
              description: courseData.description || 'No description available',
              type: courseData.type || 'Course',
              instructors: courseData.instructors || [],
              schedule: courseData.schedule || {},
              requirements: courseData.requirements || []
            });
          });
          
          console.log(`Loaded ${departmentSnapshot.size} courses from ${departmentCode}`);
        } catch (error) {
          console.warn(`Error loading department ${departmentCode}:`, error);
        }
      }
      
      console.log(`Total courses loaded: ${allCourses.length}`);
      
      if (allCourses.length === 0) {
        console.log('No courses found in any department');
        setCourses([]);
        setFilteredCourses([]);
      } else {
        // Sort courses by department and course code
        allCourses.sort((a, b) => {
          const deptCompare = (a.departmentCode || '').localeCompare(b.departmentCode || '');
          if (deptCompare !== 0) return deptCompare;
          return (a.courseCode || '').localeCompare(b.courseCode || '');
        });
        
        setCourses(allCourses);
        setFilteredCourses(allCourses);
      }
      
    } catch (error) {
      console.error('Error loading courses from Firestore:', error);
      setCourses([]);
      setFilteredCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  // Filter courses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter(course => 
        course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.courseCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (course.instructors && course.instructors.some(instructor => 
          instructor.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
      setFilteredCourses(filtered);
    }
  }, [courses, searchQuery]);

  // Add course to specific semester
  const addCourseToSemester = async (course, semesterId) => {
    if (!currentUser) return;
    
    const currentSchedule = allSchedules[semesterId] || [];
    
    // Check if course already exists
    if (currentSchedule.some(c => c.id === course.id || c.fullCourseId === course.fullCourseId)) {
      setError('Course is already in this semester');
      return;
    }
    
    const newSchedule = [...currentSchedule, { ...course, id: course.id || course.fullCourseId }];
    
    // Update local state
    setAllSchedules(prev => ({
      ...prev,
      [semesterId]: newSchedule
    }));
    
    // Auto-save
    try {
      setSaving(true);
      const scheduleRef = doc(db, `colleges/${currentUser.email.split('@')[1]}/users/${currentUser.email}/schedules/${semesterId}`);
      await setDoc(scheduleRef, {
        courses: newSchedule,
        lastUpdated: new Date(),
        term: semesterId
      });
      
      setShowAddCourseModal(false);
      setSearchQuery('');
    } catch (err) {
      console.error('Error adding course:', err);
      setError('Failed to add course');
      // Revert local state
      setAllSchedules(prev => ({
        ...prev,
        [semesterId]: currentSchedule
      }));
    } finally {
      setSaving(false);
    }
  };

  // Open add course modal for specific semester
  const openAddCourseModal = (semesterId) => {
    setSelectedSemesterForAdd(semesterId);
    setShowAddCourseModal(true);
    setSearchQuery('');
    setShowManualEntry(false);
    
    // Reset manual entry form
    setManualCourseData({
      courseCode: '',
      title: '',
      description: '',
      instructors: [''],
      requirements: [],
      departmentCode: ''
    });
    
    // Always load courses for the specific semester
    loadCourses(semesterId);
  };

  // Add manually entered course
  const addManualCourse = async () => {
    if (!manualCourseData.courseCode.trim() || !manualCourseData.title.trim()) {
      setError('Course code and title are required');
      return;
    }
    
    const course = {
      id: `${manualCourseData.courseCode.replace(/\s+/g, '')}-MANUAL-${Date.now()}`,
      fullCourseId: manualCourseData.courseCode,
      courseCode: manualCourseData.courseCode,
      title: manualCourseData.title,
      description: manualCourseData.description || 'Manually added course',
      type: 'Course',
      instructors: manualCourseData.instructors.filter(i => i.trim()),
      requirements: manualCourseData.requirements,
      departmentCode: manualCourseData.departmentCode || manualCourseData.courseCode.split(/[0-9]/)[0],
      isManualEntry: true
    };
    
    await addCourseToSemester(course, selectedSemesterForAdd);
  };

  // Handle requirement checkbox changes for manual entry
  const handleRequirementChange = (reqCode, checked) => {
    setManualCourseData(prev => ({
      ...prev,
      requirements: checked 
        ? [...prev.requirements, reqCode]
        : prev.requirements.filter(r => r !== reqCode)
    }));
  };

  // Common requirement codes for manual entry
  const commonRequirements = [
    { code: 'CW', label: 'College Writing' },
    { code: 'SCI', label: 'Science' },
    { code: 'LAB', label: 'Science Lab' },
    { code: 'SOC', label: 'Social Analysis' },
    { code: 'HUM', label: 'Humanities' },
    { code: 'ART', label: 'Arts' },
    { code: 'LANG', label: 'Foreign Language' },
    { code: 'DED', label: 'Deductive Reasoning' },
    { code: 'LIT', label: 'Literature' },
    { code: 'HIS', label: 'History' },
    { code: 'PHI', label: 'Philosophy' },
    { code: 'REL', label: 'Religion' }
  ];


  // Schedule course card component for semester view
  const ScheduleCourseCard = ({ course, semesterId }) => (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`${theme.glassCard} p-3 hover:shadow-md transition-all duration-200`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h4 className={`font-medium ${theme.text.primary} text-sm`}>
             {course.title}
          </h4>
          <p className={`text-xs ${theme.text.secondary} mt-1`}>
            {course.courseCode}
          </p>
        </div>
        <button
          onClick={() => removeCourseFromSchedule(course.id, semesterId)}
          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
        >
          <LuTrash2 className="w-3 h-3" />
        </button>
      </div>
      
      {course.schedule && course.schedule.days && course.schedule.days.length > 0 && (
        <div className={`text-xs ${theme.text.muted} flex items-center gap-1`}>
          <LuCalendar className="w-3 h-3" />
          <span>{course.schedule.days.join(", ")} • {course.schedule.timeStart}</span>
        </div>
      )}
    </motion.div>
  );


  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <motion.div
          className="rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 h-full rounded overflow-clip relative">
      {/* Header */}
      <div className={theme.spacing.section}>
        <div className="flex items-center justify-between p-4">
          <h1 className={`text-2xl font-bold font-Poppins ${theme.text.primary}`}>
            Course Planning
          </h1>
          
          {saving && (
            <div className={`flex items-center gap-2 text-sm ${theme.text.muted}`}>
              <motion.div
                className="rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              Saving...
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 my-2 bg-red-100 dark:bg-red-900 p-4 rounded-lg text-red-800 dark:text-red-200">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <LuX className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      <div className="flex-1 flex gap-6 min-h-0">
        {/* Main Schedule View - Multi-Semester */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4">
            {semesters.length === 0 ? (
              <div className="text-center py-12">
                <div className={theme.text.muted}>
                  <LuGraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  No semesters available. Please check your graduation year.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {semesters.map((semester) => {
                  const semesterSchedule = allSchedules[semester.id] || [];
                  const isExpanded = expandedSemesters.has(semester.id);
                  
                  return (
                    <motion.div
                      key={semester.id}
                      layout
                      className={`${theme.glassCard} overflow-hidden`}
                    >
                      {/* Semester Header */}
                      <div 
                        className={`${theme.glassHeader} border-b border-white/30 dark:border-white/20 p-4 cursor-pointer hover:bg-white/10 dark:hover:bg-black/10 transition-colors`}
                        onClick={() => toggleSemesterExpansion(semester.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <LuChevronDown className={`w-5 h-5 ${theme.text.secondary}`} />
                            ) : (
                              <LuChevronRight className={`w-5 h-5 ${theme.text.secondary}`} />
                            )}
                            <div>
                              <h3 className={`font-semibold ${theme.text.primary}`}>
                                {semester.displayName}
                              </h3>
                              <p className={`text-sm ${theme.text.secondary}`}>
                                {semesterSchedule.length} course{semesterSchedule.length !== 1 ? 's' : ''} {semesterSchedule.length > 0 && `• ${semesterSchedule.length * 4} credits`}
                                {semester.isCurrent && (
                                    <span
                                        className="ml-2 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full">
                                    Current
                                  </span>
                                )}
                                {semester.isUpcoming && (
                                    <span
                                        className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full">
                                    Upcoming
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Add Course Button and Credit Hours */}
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddCourseModal(semester.id);
                              }}
                              className={`${theme.glassInteractive} text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-lg transition-colors flex items-center gap-1`}
                            >
                              <LuPlus className="w-4 h-4" />
                              <span className="text-sm font-medium">Add Course</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Semester Content */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4">
                              {semesterSchedule.length === 0 ? (
                                <div className={`text-center py-8 ${theme.text.muted} text-sm`}>
                                  No courses planned for this semester yet.
                                </div>
                              ) : (
                                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                  <AnimatePresence>
                                    {semesterSchedule.map((course) => (
                                      <ScheduleCourseCard
                                        key={course.id}
                                        course={course}
                                        semesterId={semester.id}
                                      />
                                    ))}
                                  </AnimatePresence>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Distributions Checklist */}
        <DistributionsChecklist userCourses={getAllUserCourses()} />
      </div>

      {/* Add Course Modal */}
      <AnimatePresence>
        {showAddCourseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddCourseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`${theme.glassCard} shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`${theme.glassHeader} border-b border-white/30 dark:border-white/20 p-4`}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-xl font-bold ${theme.text.primary}`}>
                    Add Course to {selectedSemesterForAdd && semesters.find(s => s.id === selectedSemesterForAdd)?.displayName}
                  </h2>
                  <button
                    onClick={() => setShowAddCourseModal(false)}
                    className={`p-2 hover:bg-white/20 dark:hover:bg-black/20 rounded-lg transition-colors`}
                  >
                    <LuX className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Toggle between search and manual entry */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setShowManualEntry(false)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      !showManualEntry 
                        ? 'bg-blue-500 text-white' 
                        : `${theme.glassInteractive} ${theme.text.secondary}`
                    }`}
                  >
                    <LuSearch className="w-4 h-4" />
                    Search Courses
                  </button>
                  <button
                    onClick={() => setShowManualEntry(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      showManualEntry 
                        ? 'bg-green-500 text-white' 
                        : `${theme.glassInteractive} ${theme.text.secondary}`
                    }`}
                  >
                    <LuPencil className="w-4 h-4" />
                    Add Manually
                  </button>
                </div>
                
                {/* Search Bar or Manual Entry Form */}
                {!showManualEntry ? (
                  <div className={`relative ${theme.glassInput} p-1`}>
                    <LuSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.text.muted} w-4 h-4`} />
                    <input
                      type="text"
                      placeholder="Search courses by name, code, or instructor..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 bg-transparent ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm`}
                    />
                  </div>
                ) : (
                  <div className={`${theme.glassCard} p-4 space-y-4`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                          Course Code *
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. ECON 0155"
                          value={manualCourseData.courseCode}
                          onChange={(e) => setManualCourseData(prev => ({ ...prev, courseCode: e.target.value }))}
                          className={`w-full px-3 py-2 ${theme.glassInput} ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                          Department
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. ECON"
                          value={manualCourseData.departmentCode}
                          onChange={(e) => setManualCourseData(prev => ({ ...prev, departmentCode: e.target.value }))}
                          className={`w-full px-3 py-2 ${theme.glassInput} ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm`}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                        Course Title *
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Principles of Economics"
                        value={manualCourseData.title}
                        onChange={(e) => setManualCourseData(prev => ({ ...prev, title: e.target.value }))}
                        className={`w-full px-3 py-2 ${theme.glassInput} ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                        Description
                      </label>
                      <textarea
                        placeholder="Course description..."
                        value={manualCourseData.description}
                        onChange={(e) => setManualCourseData(prev => ({ ...prev, description: e.target.value }))}
                        rows="2"
                        className={`w-full px-3 py-2 ${theme.glassInput} ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm resize-none`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                        Instructor
                      </label>
                      <input
                        type="text"
                        placeholder="Professor name..."
                        value={manualCourseData.instructors[0] || ''}
                        onChange={(e) => setManualCourseData(prev => ({ 
                          ...prev, 
                          instructors: [e.target.value, ...prev.instructors.slice(1)]
                        }))}
                        className={`w-full px-3 py-2 ${theme.glassInput} ${theme.text.primary} placeholder-gray-400 focus:outline-none text-sm`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-xs font-medium ${theme.text.primary} mb-2`}>
                        Requirements Fulfilled
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {commonRequirements.map(req => (
                          <label key={req.code} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={manualCourseData.requirements.includes(req.code)}
                              onChange={(e) => handleRequirementChange(req.code, e.target.checked)}
                              className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className={`text-xs ${theme.text.secondary}`}>{req.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <button
                      onClick={addManualCourse}
                      disabled={!manualCourseData.courseCode.trim() || !manualCourseData.title.trim()}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg py-2 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <LuPlus className="w-4 h-4" />
                      Add Course to Schedule
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {!showManualEntry ? (
                  coursesLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <motion.div
                        className="rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    </div>
                  ) : filteredCourses.length === 0 ? (
                    <div className="text-center py-12">
                      <div className={`${theme.text.muted} text-sm mb-4`}>
                        {searchQuery
                          ? "No courses match your search criteria." 
                          : `Course data for ${selectedSemesterForAdd && semesters.find(s => s.id === selectedSemesterForAdd)?.displayName} is not available.`}
                      </div>
                      <div className={`${theme.text.muted} text-xs`}>
                        Try using "Add Manually" to plan courses for this semester.
                      </div>
                    </div>
                  ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    <AnimatePresence>
                      {filteredCourses.slice(0, 20).map((course) => (
                        <motion.div
                          key={course.id || course.fullCourseId}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className={`${theme.glassCard} p-4 hover:shadow-md transition-all duration-200`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className={`font-medium ${theme.text.primary} text-sm`}>
                                {course.title}
                              </h3>
                              <p className={`text-xs ${theme.text.secondary} mt-1`}>
                                {course.courseCode} • {course.type || 'Course'}
                              </p>
                            </div>
                            <button
                              onClick={() => addCourseToSemester(course, selectedSemesterForAdd)}
                              className="bg-blue-500/80 hover:bg-blue-600/80 backdrop-blur-lg text-white rounded-lg px-3 py-1 text-xs font-medium transition-colors flex items-center gap-1"
                            >
                              <LuPlus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                          
                          {course.description && (
                            <p className={`text-xs ${theme.text.muted} mb-2 line-clamp-2`}>
                              {course.description}
                            </p>
                          )}
                          
                          <div className={`space-y-1 text-xs ${theme.text.muted}`}>
                            {course.instructors && course.instructors.length > 0 && (
                              <div className="flex items-center gap-1">
                                <LuUser className="w-3 h-3" />
                                <span>{course.instructors[0]}</span>
                              </div>
                            )}
                            {course.schedule && course.schedule.days && course.schedule.days.length > 0 && (
                              <div className="flex items-center gap-1">
                                <LuCalendar className="w-3 h-3" />
                                <span>{course.schedule.days.join(", ")} • {course.schedule.timeStart}</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <div className={`${theme.text.muted} text-sm mb-4`}>
                      <LuBook className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      Use the form above to manually add courses for planning purposes.
                    </div>
                    <div className={`${theme.text.muted} text-xs`}>
                      Perfect for upcoming semesters or when course data isn't available yet.
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Graduation Year Modal */}
      <GradYearModal
        isOpen={showGradYearModal}
        onClose={() => setShowGradYearModal(false)}
        onSubmit={handleGradYearSubmit}
        currentUser={currentUser}
      />
    </div>
  );
};

export default ScheduleBuilderPage;